class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);

    this.setViewportHeight();
    window.addEventListener('resize', this.onViewportResize);

    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    window.removeEventListener('resize', this.onViewportResize);
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setViewportHeight() {
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
  }

  onViewportResize = () => {
    if (this.classList.contains('active')) {
      this.setViewportHeight();
    }
  };

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      
      // Use section.section (the Shopify section ID) to get the HTML, fallback to section.id
      const sectionKey = section.section || section.id;
      const sectionHTML = parsedState.sections?.[sectionKey];
      if (!sectionHTML) return;
      
      const innerHTML = this.getSectionInnerHTML(sectionHTML, section.selector);
      if (innerHTML) {
        sectionElement.innerHTML = innerHTML;
      }
    });

    reinitializeWishlistHeroButtons(this);

    // Update cart count bubble
    this.updateCartCountBubble(parsedState);

    const isCartPage = document.body.classList.contains('cart') || (typeof window !== 'undefined' && window.location && window.location.pathname && window.location.pathname.includes('/cart'));

    if (isCartPage) {
      // On cart page: refresh and load from top to show the newly added item (reliable for quick-add)
      if ('scrollRestoration' in history) history.scrollRestoration = 'manual';
      window.location.href = window.location.pathname + (window.location.search || '');
      return;
    }

    // Not on cart page: update drawer is-empty and open it
    const cartDrawerEl = document.querySelector('cart-drawer');
    const itemCount = parsedState.item_count ?? parsedState.cart?.item_count ?? (parsedState.items ? parsedState.items.length : undefined);
    if (itemCount !== undefined && itemCount !== null && cartDrawerEl) {
      cartDrawerEl.classList.toggle('is-empty', itemCount === 0);
    } else if (cartDrawerEl) {
      cartDrawerEl.classList.remove('is-empty');
    }
    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }


  getSectionInnerHTML(html, selector = '.shopify-section') {
    if (!html) return null;
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const element = parsed.querySelector(selector);
    return element ? element.innerHTML : null;
  }

  updateCartCountBubble(parsedState) {
    if (!parsedState) {
      return;
    }
    
    // Get item_count from different possible locations in the response
    let itemCount = parsedState.item_count 
      || parsedState.cart?.item_count 
      || (parsedState.items ? parsedState.items.length : undefined);
    
    // If itemCount is not available, try to extract it from cart-drawer section HTML
    if (itemCount === undefined && parsedState.sections && parsedState.sections['cart-drawer']) {
      const cartDrawerHTML = parsedState.sections['cart-drawer'];
      const htmlDoc = new DOMParser().parseFromString(cartDrawerHTML, 'text/html');
      
      // Try to find item count from drawer header
      const itemCountElement = htmlDoc.querySelector('.drawer__item-count');
      if (itemCountElement) {
        const countText = itemCountElement.textContent.trim();
        const match = countText.match(/(\d+)/);
        if (match) {
          itemCount = parseInt(match[1], 10);
        }
      }
      
      // If still not found, count cart items
      if (itemCount === undefined) {
        const cartItems = htmlDoc.querySelectorAll('.cart-item, [id^="CartDrawer-Item-"]');
        if (cartItems.length > 0) {
          // Sum up quantities from all items
          let totalQuantity = 0;
          cartItems.forEach(item => {
            const quantityInput = item.querySelector('input[name="updates[]"]');
            if (quantityInput) {
              totalQuantity += parseInt(quantityInput.value || 0, 10);
            }
          });
          if (totalQuantity > 0) {
            itemCount = totalQuantity;
          }
        }
      }
    }
    
    // If still not found, fetch cart data
    if (itemCount === undefined) {
      fetch(`${routes.cart_url}.js`)
        .then(response => response.json())
        .then(cartData => {
          const fetchedItemCount = cartData.item_count;
          if (fetchedItemCount !== undefined) {
            this.updateCartCountBubbleElement(fetchedItemCount);
          }
        })
        .catch(error => {
        });
      return; // Return early, will update via callback
    }
    
    // Update cart count bubble directly using item_count
    this.updateCartCountBubbleElement(itemCount);
    
    // Also try to update via header section if available (this will override the direct update if section has more accurate data)
    if (parsedState.sections) {
      // Try header section first
      const headerSectionId = Object.keys(parsedState.sections).find(key => key.includes('header'));
      if (headerSectionId) {
        this.updateCartCountBubbleFromSection(parsedState.sections[headerSectionId]);
      }
      // Also try cart-icon-bubble section if it exists
      if (parsedState.sections['cart-icon-bubble']) {
        this.updateCartCountBubbleFromSection(parsedState.sections['cart-icon-bubble']);
      }
    }
  }

  updateCartCountBubbleElement(itemCount) {
    if (itemCount === undefined || itemCount === null) {
      return;
    }
    
    // Update cart count bubble directly using item_count
    if (itemCount !== undefined) {
      // Try to find cart icon bubble element
      const cartIconBubble = document.getElementById('cart-icon-bubble');
      if (!cartIconBubble) {
        return;
      }
      
      let cartCountBubble = cartIconBubble.querySelector('.cart-count-bubble');
      
      if (itemCount > 0) {
        // If cart count bubble doesn't exist, create it
        if (!cartCountBubble) {
          cartCountBubble = document.createElement('div');
          cartCountBubble.className = 'cart-count-bubble';
          cartIconBubble.appendChild(cartCountBubble);
        }
        
        // Update or create the count span
        let countSpan = cartCountBubble.querySelector('span[aria-hidden="true"]');
        
        if (!countSpan) {
          countSpan = document.createElement('span');
          countSpan.setAttribute('aria-hidden', 'true');
          cartCountBubble.insertBefore(countSpan, cartCountBubble.firstChild);
        }
        
        const countValue = itemCount < 100 ? itemCount : '99+';
        countSpan.textContent = countValue;
        
        cartCountBubble.style.display = '';
        
        // Update or create visually hidden span
        let visuallyHiddenSpan = cartCountBubble.querySelector('.visually-hidden');
        
        if (!visuallyHiddenSpan) {
          visuallyHiddenSpan = document.createElement('span');
          visuallyHiddenSpan.className = 'visually-hidden';
          cartCountBubble.appendChild(visuallyHiddenSpan);
        }
        
        if (window.cartStrings && window.cartStrings.cartCount) {
          const cartCountText = window.cartStrings.cartCount;
          visuallyHiddenSpan.textContent = cartCountText.replace('[count]', itemCount).replace('{{ count }}', itemCount);
        } else {
          visuallyHiddenSpan.textContent = `${itemCount} items`;
        }
      } else {
        // Hide cart count bubble if cart is empty
        if (cartCountBubble) {
          cartCountBubble.style.display = 'none';
        }
      }
    }
  }

  updateCartCountBubbleFromSection(sectionHTML) {
    if (!sectionHTML) return;
    
    const cartIconBubble = document.getElementById('cart-icon-bubble');
    if (!cartIconBubble) return;
    
    const htmlDoc = new DOMParser().parseFromString(sectionHTML, 'text/html');
    
    // Try multiple selectors to find the cart icon bubble
    const newCartIconBubble = htmlDoc.getElementById('cart-icon-bubble') 
      || htmlDoc.querySelector('#cart-icon-bubble')
      || htmlDoc.querySelector('a#cart-icon-bubble');
    
    if (newCartIconBubble) {
      // Update the entire cart icon bubble content
      cartIconBubble.innerHTML = newCartIconBubble.innerHTML;
      
      // Also ensure the cart count bubble is visible if there are items
      const cartCountBubble = cartIconBubble.querySelector('.cart-count-bubble');
      if (cartCountBubble) {
        const itemCount = parseInt(cartCountBubble.querySelector('span[aria-hidden="true"]')?.textContent || '0');
        if (itemCount > 0) {
          cartCountBubble.style.display = '';
        } else {
          cartCountBubble.style.display = 'none';
        }
      }
    } else {
      // Try to get from section wrapper (for header section)
      const sectionWrapper = htmlDoc.querySelector('.shopify-section') || htmlDoc.querySelector('section');
      if (sectionWrapper) {
        const bubbleInSection = sectionWrapper.querySelector('#cart-icon-bubble') || sectionWrapper.querySelector('a#cart-icon-bubble');
        if (bubbleInSection) {
          cartIconBubble.innerHTML = bubbleInSection.innerHTML;
          
          // Also ensure the cart count bubble is visible if there are items
          const cartCountBubble = cartIconBubble.querySelector('.cart-count-bubble');
          if (cartCountBubble) {
            const itemCount = parseInt(cartCountBubble.querySelector('span[aria-hidden="true"]')?.textContent || '0');
            if (itemCount > 0) {
              cartCountBubble.style.display = '';
            } else {
              cartCountBubble.style.display = 'none';
            }
          }
        }
      }
    }
  }

  getSectionsToRender() {
    const sections = [
      {
        id: 'cart-drawer',
        section: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
      },
    ];
    // On cart page: also request main-cart-items so we can update the page without opening the drawer
    if (document.body.classList.contains('cart') || (typeof window !== 'undefined' && window.location && window.location.pathname && window.location.pathname.includes('/cart'))) {
      const mainCartItems = document.getElementById('main-cart-items');
      if (mainCartItems && mainCartItems.dataset.id) {
        sections.push({
          id: mainCartItems.dataset.id,
          section: mainCartItems.dataset.id,
          selector: 'cart-items .page-width',
        });
      }
    }
    return sections;
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }

  updateQuantity(line, quantity, name, variantId) {
    // Call parent updateQuantity which handles the cart count bubble update
    super.updateQuantity(line, quantity, name, variantId);
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
